import React, { useEffect, useState } from "react";
import ShowToast from "../Common/Toast";
import { useParams, useLocation } from "react-router-dom";
import useVendorStore from "../HomePage/useVendorStore";
import NoEmailBottomSheet from "./NoEmailBottomSheet";
import { Loader } from "../Common/Loader";
import { Button, Details, WhiteWrapper } from "../Common/styles.common";
import { GrnPageWrapper, GrnSummaryCard, GrnSummaryMetric, GrnSummaryMetrics, GrnSummarySectionWrapper } from "./styles.grn";
import BackButton from "../Button/BackButton";
import GrnDetailsSummaryTable from "./GrnDetailsSummary/GrnDetailsSummaryTable";
import Spinner from "sushiweb/Spinner/Spinner";
import { STATUS_BEGIN, STATUS_DONE } from "utils/constants/others";
import useGrnDisputeLogic from "./useGrnDisputeLogic";
import GrnDisputeItems from "./GrnDisputeItems";
import { strings } from "utils/constants/strings";

const RaiseDisputePage = () => {
  const { source, purchaseOrderId, grnId } = useParams();
  const location = useLocation();

  const getPoGrnMappings = useVendorStore((state) => state.getPoGrnMappings);
  const getPoGrnMappingStatus = useVendorStore((state) => state.getPoGrnMappingStatus);
  const poGrnList = useVendorStore((state) => state.poGrnList);
  const [grn, setGrn] = useState(null);
  const [grnItems, setGrnItems] = useState([]);
  const [showLoader, setShowLoader] = useState(false);


  // Email verification flow
  const [showNoEmailSheet, setShowNoEmailSheet] = useState(false);

  // Clear restored state from browser history so a refresh doesn't re-apply it
  useEffect(() => {
    if (location.state?.restoredDisputeDescription !== undefined ||
      location.state?.restoredDisputeItemIssues !== undefined) {
      window.history.replaceState({}, "");
    }
  }, []);

  useEffect(() => {
    getPoGrnMappings(
      {
        po_grn_mapping_id: grnId,
        is_po_grn_items_required: true,
        get_cn_data: true,
        fetch_seller_status_meta_data: true,
        fetch_ledger_data: true,
        get_prn_data: true,
        is_payment_details_required: true,
        skip_total_page_count: true,
        fetch_vendor_dispute_data: true,
      },
      true
    );
  }, []);

  useEffect(() => {
    switch (getPoGrnMappingStatus) {
      case STATUS_BEGIN:
        setShowLoader(true);
        break;
      case STATUS_DONE:
        setShowLoader(false);
        break;
    }
  }, [getPoGrnMappingStatus]);

  useEffect(() => {
    if (getPoGrnMappingStatus === STATUS_DONE && poGrnList) {
      setGrn(poGrnList[0]);
    }
  }, [poGrnList]);

  useEffect(() => {
    setGrnItems(Array.isArray(grn?.po_grn_items) ? grn.po_grn_items : []);
  }, [grn]);

  const grnSummary = grn?.grn_page_data?.summary;

  const {
    disputeIssueDescription,
    disputeItemIssues,
    submitDisputeLoading,
    showToast,
    setShowToast,
    errorMsg,
    handleSubmitDispute,
    ...disputeItemsProps
  } = useGrnDisputeLogic({
    grn,
    grnItems,
    source,
    purchaseOrderId,
    grnId,
    restored: {
      description: location.state?.restoredDisputeDescription,
      itemIssues: location.state?.restoredDisputeItemIssues,
    },
    onNeedEmail: () => setShowNoEmailSheet(true),
  });

  const backPath =
    source === "all"
      ? `/grn/details/all/${purchaseOrderId}/${grnId}`
      : `/grn/details/list/${purchaseOrderId}/${grnId}`;


  return (
    <React.Fragment>
      {showLoader ? (
        <Loader />
      ) : (
        <GrnPageWrapper className="d-flex flex-column">
          {/* Scrollable body */}
          <div className="d-flex flex-column y-scroll" style={{ flex: 1 }}>
            {/* Header — back button + title (no header tag strip) */}
            <Details $flatBottom>
              <div className="d-flex align-items-center ml-4">
                <BackButton pathName={backPath} />
                <div className="d-flex flex-column ml-1 mr-4">
                  <span className="fs-18 fw-700">{strings.grnDisputePageHeading}</span>
                  {grn?.grn_number && (
                    <span className="fs-12 fw-400 text-gray-cool mt-1">
                      {grn.grn_number}
                    </span>
                  )}
                </div>
              </div>
            </Details>

            {/* GRN Summary */}
            {grnSummary && (
              <GrnSummarySectionWrapper style={{ marginTop: "16px" }}>
                <GrnSummaryCard>
                  <GrnSummaryMetrics>
                    <GrnSummaryMetric>
                      <span className="fs-18 fw-600">
                        {grnSummary?.invoice_amount?.value}
                      </span>
                      <span className="fs-13 fw-400 text-gray-cool">
                        {grnSummary?.invoice_amount?.label}
                      </span>
                    </GrnSummaryMetric>
                    <GrnSummaryMetric>
                      <span className="fs-18 fw-600">
                        {grnSummary?.grn_amount?.value}
                      </span>
                      <span className="fs-13 fw-400 text-gray-cool">
                        {grnSummary?.grn_amount?.label}
                      </span>
                    </GrnSummaryMetric>
                  </GrnSummaryMetrics>
                  <GrnDetailsSummaryTable rows={grnSummary?.rows} />
                </GrnSummaryCard>
              </GrnSummarySectionWrapper>
            )}

            {/* "Raise a dispute" heading */}
            <div style={{ padding: "16px 16px 0 16px" }}>
              <span
                style={{
                  fontWeight: 700,
                  fontSize: "20px",
                  lineHeight: "26px",
                  letterSpacing: "-0.01em",
                  color: "#293142",
                }}
              >
                {strings.grnDisputePageHeading}
              </span>
            </div>

            <GrnDisputeItems
              {...disputeItemsProps}
              disputeIssueDescription={disputeIssueDescription}
              disputeItemIssues={disputeItemIssues}
            />
          </div>

          {/* Sticky bottom CTA */}
          <WhiteWrapper className="px-5">
            <Button
              className="fs-13 text-white fw-600 mb-3"
              onClick={handleSubmitDispute}
              disabled={submitDisputeLoading}
            >
              {submitDisputeLoading ? (
                <Spinner color="white" />
              ) : (
                strings.grnDisputeSubmitButton
              )}
            </Button>
          </WhiteWrapper>
          {showNoEmailSheet && (
            <NoEmailBottomSheet
              isOpen={showNoEmailSheet}
              onClose={() => setShowNoEmailSheet(false)}
              source={source}
              purchaseOrderId={purchaseOrderId}
              grnId={grnId}
              formState={{ disputeIssueDescription, disputeItemIssues }}
            />
          )}
        </GrnPageWrapper>
      )}
      {showToast && errorMsg && (
        <ShowToast
          errorMsg={errorMsg}
          type="error"
          onClose={() => setShowToast(false)}
        />
      )}
    </React.Fragment>
  );
};

export default RaiseDisputePage;
